/**
 * Tooltip - shared Radix-based tooltip.
 *
 * Industry-standard accessible tooltip (WAI-ARIA compliant, portal rendered,
 * auto-flip to stay in viewport, keyboard/focus support).
 *
 * Usage:
 *   <Tooltip content="Explanation">
 *     <Info size={12} />
 *   </Tooltip>
 *
 * Props:
 *   content   - string or node shown inside the tooltip. Falsy => children render as-is.
 *   children  - the trigger element; forwarded via asChild so no wrapper DOM.
 *   side      - 'top' | 'right' | 'bottom' | 'left' (default 'top').
 *   align     - 'start' | 'center' | 'end' (default 'center').
 *   delay     - open delay in ms (default 200).
 *   sideOffset- gap from trigger in px (default 6).
 */

import * as RadixTooltip from '@radix-ui/react-tooltip';

const Tooltip = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = 200,
  sideOffset = 6,
}) => {
  if (!content) return children;

  return (
    <RadixTooltip.Provider delayDuration={delay}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            align={align}
            sideOffset={sideOffset}
            className="z-[60] max-w-xs rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal leading-snug text-white shadow-lg data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0"
          >
            {content}
            <RadixTooltip.Arrow className="fill-slate-800" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

export default Tooltip;
